import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { NatureStore } from '@/stores/NatureStore';
import { orderByField } from '@/utils/orderBy';
import type { INatureInfo } from '~/api/INatureInfo';
import type { INatureMsg } from '~/api/messages/INatureMsg';
import type { INatureUpdateRespMsg } from '~/api/messages/INatureUpdateRespMsg';

@SignalrListener
@service(NatureManager)
export class NatureManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(NatureStore) protected natureStore: NatureStore;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  find(id: string) {
    return this.natureStore.find(id);
  }

  get all() {
    const natures = this.natureStore.all()
      .filter((nature) => nature.status !== STATUS.DISABLED);
    return orderByField(natures, this.natureStore.valueKey);
  }

  reset() {
    this.loaded = false;
    this.natureStore.set([]);
  }

  updateNatures(natures: INatureInfo[]) {
    this.loaded = true;
    this.natureStore.batchUpdate(natures);
  }

  @SignalrMessage('NatureMsg')
  onNatureMsg(response: INatureMsg) {
    this.updateNatures(response.natures);
  }

  @SignalrMessage('NatureUpdateRespMsg')
  onNatureUpdateRespMsg(response: INatureUpdateRespMsg) {
    this.updateNatures([response.nature]);
  }
}
